<template>
  <q-dialog v-model="shown">
    <q-card
      class="q-pl-md"
      style="width: 100%; max-width: 950px;"
    >
      <q-card-section class="row items-center q-mx-xs">
        <div class="text-h6">
          Report Element Information
        </div>
        <q-space />

        <q-btn
          v-close-popup
          icon="close"
          flat
          round
          dense
        />
      </q-card-section>
      <q-card-section class="row">
        <div class="col-4">
          <category-list
            class="q-mb-md"
            title="Data Source"
            hide-category
            icon="fas fa-database"
            :items="{
              'Data Source': [{
                label: reportOptions.data_source_name,
                definition: dataSourceDescription()
              }]
            }"
            empty-message="No data source selected"
            show-edit-button
            @edit="$emit('edit-element')"
          />
          <div v-if="reportOptions.effective_type || reportElement.controls.includes('effective_type')">
            <category-list
              class="q-mb-md"
              title="Effective Type"
              hide-category
              icon="calendar_month"
              :items="effectiveTypeItem"
              empty-message="No effective type selected"
              show-edit-button
              @edit="$emit('edit-effective-type')"
            />
            <q-tooltip>
              <effective-type-detail
                style="max-width: 700px;"
                :effective-type="reportOptions.effective_type || 'accrual'"
                :show-void-control="false"
              />
            </q-tooltip>
          </div>
          <div v-else>
            <category-list
              class="q-mb-md"
              title="Date Range"
              hide-category
              icon="calendar_month"
              :items="dateRangeItem"
              empty-message="No date range selected"
              show-edit-button
              @edit="$emit('edit-element')"
            />
          </div>
          <div>
            <category-list
              class="q-mb-md"
              title="Group By Fields"
              :items="groupByColumns"
              empty-message="No grouping fields selected"
              icon="category"
              show-edit-button
              @edit="$emit('edit-groupings')"
            />
          </div>
        </div>

        <div class="col-8 q-pl-md">
          <div>
            <category-list
              title="Column Fields"
              :items="columns"
              empty-message="No columns available"
              icon="view_column"
              show-edit-button
              @edit="$emit('edit-columns')"
            />
          </div>
        </div>
      </q-card-section>
    </q-card>
  </q-dialog>
</template>

<script>
import _ from 'lodash'
import { EFFECTIVE_TYPE_DESCRIPTIONS } from 'src/models/accounting/EffectiveTypeDescriptions'
import { IsoDate } from 'src/models/Model'
import EffectiveTypeDetail from 'src/modules/admin/effective-type/EffectiveTypeDetail'
import CategoryList from 'src/modules/admin/report-table/CategoryList.vue'

export default {
  name: 'ReportTableInformationDialog',
  components: {
    CategoryList,
    EffectiveTypeDetail
  },
  props: {
    modelValue: {
      type: Boolean,
      required: false,
      default: false
    },
    reportElement: {
      type: Object,
      required: true
    },
    reportOptions: {
      type: Object,
      required: true
    },
    visibleColumns: {
      type: Array,
      required: true
    }
  },
  emits: ['update:model-value', 'edit-element', 'edit-columns', 'edit-groupings', 'edit-effective-type'],
  data () {
    return {
      shown: this.modelValue
    }
  },
  computed: {
    columns () {
      const allowedFields = this.reportOptions.column_order?.length
        ? this.reportOptions.column_order
        : this.visibleColumns.map(col => col.field)

      const columns = this.reportElement.columns.filter(col => allowedFields.includes(col.field))

      const grouped = {}

      columns.forEach(column => {
        const category = column.category || 'Other'
        if (!grouped[category]) {
          grouped[category] = []
        }
        grouped[category].push(column)
      })

      return grouped
    },
    dateRangeItem () {
      const startDate = this.reportOptions.start_date || this.reportElement.finalOptions?.start_date || this.reportElement.defaultOptions?.start_date
      const endDate = this.reportOptions.end_date || this.reportElement.finalOptions?.end_date || this.reportElement.defaultOptions?.end_date
      return {
        'Date Range': [{
          label: ((startDate && IsoDate.parse(startDate).i18n('shortDate')) || '') +
                   (endDate ? ' to ' : '') +
                   ((endDate && IsoDate.parse(endDate).i18n('shortDate')) || '')
        }]
      }
    },
    effectiveTypeItem () {
      if (!this.reportOptions.effective_type) {
        // Default effective type is accrual
        return {
          'Effective Type': [{
            label: 'Accrual',
            definition: EFFECTIVE_TYPE_DESCRIPTIONS.accrual
          }]
        }
      }

      return {
        'Effective Type': [{
          label: _.startCase(this.reportOptions.effective_type),
          definition: EFFECTIVE_TYPE_DESCRIPTIONS[this.reportOptions.effective_type] || this.reportOptions.effective_type
        }]
      }
    },
    groupByColumns () {
      if (!this.reportOptions.group_by?.length) {
        return {}
      }

      const grouped = {}
      const possibleOptions = this.reportElement.possibleOptionsValues?.group_by || []
      possibleOptions.forEach(value => {
        const isNamedValue = value.name || value.label
        const optionValue = isNamedValue ? (value.value || value.id) : value
        if (!this.reportOptions.group_by.includes(optionValue)) {
          return
        }
        const columnOption = _.find(this.reportElement.columns, { field: optionValue })
        if (columnOption) {
          const category = columnOption.category || 'Other'
          if (!grouped[category]) {
            grouped[category] = []
          }
          grouped[category].push(columnOption)
        }
      })
      return grouped
    }
  },
  watch: {
    modelValue (val) {
      this.shown = val
    },
    shown (val) {
      this.$emit('update:model-value', val)
    }
  },
  methods: {
    dataSourceDescription () {
      const dataSourceOption = this.reportElement.possibleOptionsValues?.data_source_name_options?.find(
        option => option.value === this.reportOptions.data_source_name
      )
      return dataSourceOption?.description || ''
    },
    getColumnLabel (field) {
      if (this.reportOptions.group_by?.includes(field)) {
        const columnOption = _.find(this.reportElement.columns || [], { field })
        if (columnOption) {
          return columnOption.label || _.startCase(field)
        }
      }
      const column = this.visibleColumns.find(col => col.field === field)
      return column ? column.label : _.startCase(field)
    },
    getColumnDefinition (field) {
      if (this.reportOptions.group_by?.includes(field)) {
        const columnOption = _.find(this.reportElement.columns, { field })
        if (columnOption) {
          return columnOption.definition || ''
        }
      }
      const column = this.visibleColumns.find(col => col.field === field)
      return column?.definition || ''
    }
  }
}
</script>
